// src/utils/textEncodingUtils.ts

/**
 * Corrige l'encodage UTF-8 défaillant dans un texte
 * Utilise une approche par regex pour éviter les doublons de clés
 */
export function fixTextEncoding(text: string | null | undefined): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  let correctedText = text;

  // Corrections par regex - méthode plus sûre
  const corrections = [
    // Caractères accentués courants
    [/Ã©/g, 'é'],
    [/Ã¨/g, 'è'],
    [/Ã¡/g, 'á'],
    [/Ã /g, 'à'],
    [/Ã¢/g, 'â'],
    [/Ã§/g, 'ç'],
    [/Ã´/g, 'ô'],
    [/Ã¶/g, 'ö'],
    [/Ã¹/g, 'ù'],
    [/Ã»/g, 'û'],
    [/Ã¼/g, 'ü'],
    [/Ãª/g, 'ê'],
    [/Ã«/g, 'ë'],
    [/Ã®/g, 'î'],
    [/Ã¯/g, 'ï'],
    [/Ãº/g, 'ú'],
    [/Ã½/g, 'ý'],
    [/Ã±/g, 'ñ'],
    
    // Versions majuscules
    [/Ã‰/g, 'É'],
    [/Ãˆ/g, 'È'],  
    [/Ã€/g, 'À'],
    [/Ã‡/g, 'Ç'],
    [/ÃŠ/g, 'Ê'],
    [/Ã‹/g, 'Ë'],
    [/ÃŒ/g, 'Ì'],
    [/ÃŽ/g, 'Î'],
    [/Ã™/g, 'Ù'],
    [/Ã›/g, 'Û'],
    
    // Caractères spéciaux
    [/â€™/g, "'"],      // Apostrophe courbe
    [/â€œ/g, '"'],      // Guillemet ouvrant
    [/â€/g, '"'],       // Guillemet fermant
    [/â€"/g, '–'],      // Tiret
    [/â€¦/g, '...'],    // Points de suspension
    [/â‚¬/g, '€'],      // Euro
    [/Â°/g, '°'],       // Degré
    [/Â©/g, '©'],       // Copyright
    [/Â®/g, '®'],       // Registered
    [/â„¢/g, '™'],      // Trademark
    
    // Espaces
    [/Â /g, ' '],       // Espace insécable
    [/â€Š/g, ' '],      // Espace fine
    [/â€ƒ/g, ' '],      // Espace cadratin
  ];

  // Appliquer toutes les corrections
  corrections.forEach(([regex, replacement]) => {
    correctedText = correctedText.replace(regex, replacement as string);
  });

  // Nettoyer les caractères résiduels
  correctedText = correctedText
    // Supprimer les caractères de contrôle invisibles
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, '')
    // Normaliser les espaces multiples
    .replace(/\s+/g, ' ')
    // Supprimer les espaces en début/fin
    .trim();

  return correctedText;
}

/**
 * Corrige l'encodage de tous les champs texte d'un objet
 */
export function fixObjectEncoding<T extends Record<string, any>>(
  obj: T, 
  fields?: (keyof T)[]
): T {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const correctedObj = { ...obj };
  const fieldsToFix = fields || Object.keys(obj).filter(key => 
    typeof obj[key] === 'string'
  );

  fieldsToFix.forEach(field => {
    if (typeof correctedObj[field] === 'string') {
      correctedObj[field] = fixTextEncoding(correctedObj[field] as string) as T[typeof field];
    }
  });

  return correctedObj;
}

/**
 * Détecte si un texte contient des problèmes d'encodage
 */
export function hasEncodingIssues(text: string | null | undefined): boolean {
  if (!text || typeof text !== 'string') {
    return false;
  }

  const patterns = [
    /Ã[¡-¿]/,           // Caractères accentués mal encodés
    /â€[œ""]/,          // Guillemets et apostrophes
    /â€[—–¦]/,          // Tirets et points
    /â‚¬/,              // Euro
    /Â[°©®\s]/,         // Caractères spéciaux avec Â
    /â„¢/,              // Trademark
  ];

  return patterns.some(pattern => pattern.test(text));
}

/**
 * Fonction spécialisée pour les libellés de dossiers
 * Applique des corrections spécifiques au contexte métier
 */
export function fixDossierLibelle(libelle: string | null | undefined): string {
  const corrected = fixTextEncoding(libelle);
  
  // Corrections spécifiques au contexte des dossiers
  return corrected
    .replace(/PrÃ©judiciaire/gi, 'Préjudiciaire')
    .replace(/procÃ©dure/gi, 'procédure')
    .replace(/crÃ©ance/gi, 'créance')
    .replace(/dÃ©biteur/gi, 'débiteur')
    .replace(/rÃ©glÃ©/gi, 'réglé')
    .replace(/soldÃ©/gi, 'soldé')
    .replace(/encaissÃ©/gi, 'encaissé')
    .replace(/relancÃ©/gi, 'relancé')
    .replace(/contentieux/gi, 'contentieux')
    .replace(/rÃ©cupÃ©ration/gi, 'récupération');
}

// Export par défaut de la fonction principale
export default fixTextEncoding;